/**
 * fp-ts's `map` works with custom equality and ordering, which makes many operations cost O(n)
 * that costs O(1) on a classic map, including basic `get` and `set` methods.
 * This module provides functional code for working with a map that assumes === for equality
 * and no ordering (which may break some purity principles like consistent output from conversion
 * to array, but O(n log n) complexity of ordering is often not worth that.
 */
import { option, readonlyArray } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/function';

import { branch } from './function';

const fromArray = <K, V>(
  getKey: (value: V) => K,
): ((source: readonly V[]) => Map<K, V>) =>
  readonlyArray.reduce(new Map(), (result, value) => {
    result.set(getKey(value), value);
    return result;
  });

const filterMapToArray = <K, V, R>(
  f: (current: [K, V]) => Option<R>,
): ((map: Map<K, V>) => R[]) =>
  reduce<K, V, R[]>([], (acc, elem) => {
    pipe(
      elem,
      f,
      option.map((r) => acc.push(r)),
    );
    return acc;
  });

const filterMutable =
  <K, V>(f: (value: V, key: K) => boolean) =>
  (m: Map<K, V>): Map<K, V> => {
    m.forEach((value, key) => {
      if (!f(value, key)) {
        m.delete(key);
      }
    });
    return m;
  };

const get =
  <K>(key: K) =>
  <V>(map: Map<K, V>): Option<V> =>
    map.has(key)
      ? option.some(map.get(key)!) // it is not undefined since map.has(key)
      : option.none;

const getFrom =
  <K, V>(map: Map<K, V>) =>
  (key: K): Option<V> =>
    get(key)(map);

/**
 * Get value under a key from map, or create it and store under the key
 */
const getOrCreate =
  <K, V>(key: K, onNone: () => V) =>
  (map: Map<K, V>): V =>
    map.has(key)
      ? map.get(key)!
      : pipe(
          onNone(),
          branch((value) => map.set(key, value)),
        );

const toggle =
  <K, V>(key: K, onAdd: () => V) =>
  (map: Map<K, V>): Map<K, V> => {
    const newMap = new Map(map.entries());
    if (newMap.has(key)) {
      newMap.delete(key);
    } else {
      newMap.set(key, onAdd());
    }
    return newMap;
  };

const insertOrUpdateMutable =
  <K, V>(key: K, insert: () => V, update: (existing: V) => V) =>
  (map: Map<K, V>): Map<K, V> => {
    map.set(key, map.has(key) ? update(map.get(key)!) : insert());
    return map;
  };

const popMutable =
  <K>(key: K) =>
  <V>(map: Map<K, V>): Option<V> => {
    const maybeElement = pipe(map, get(key));
    map.delete(key);
    return maybeElement;
  };

const popFromMutable =
  <K, V>(map: Map<K, V>) =>
  (key: K): Option<V> => {
    const maybeElement = pipe(map, get(key));
    map.delete(key);
    return maybeElement;
  };

const collect =
  <K, A, B>(f: (k: K, a: A) => B) =>
  (m: Map<K, A>): B[] => {
    const result: B[] = [];
    m.forEach((value, key) => {
      result.push(f(key, value));
    });
    return result;
  };

const reduce =
  <K, V, R>(initialValue: R, reducer: (acc: R, current: [K, V]) => R) =>
  (map: Map<K, V>): R =>
    Array.from(map.entries()).reduce(reducer, initialValue);

export const plainMap = {
  fromArray,

  filterMapToArray,
  filterMutable,

  get,
  getFrom,
  getOrCreate,
  insertOrUpdateMutable,
  popMutable,
  popFromMutable,
  toggle,

  collect,
  reduce,
};
